import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid,
    IconButton, MenuItem, Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import {
    ActivityNode, ExecutionOverview, ExecutionTask, TASK_PRIORITY_LABELS, TASK_STATUS_LABELS, TaskPriority, TaskStatus,
} from '../../../types/execution';
import executionService, { TaskPayload } from '../../../services/executionService';
import { errorMessage, formatDate } from './executionUtils';

type Notify = (message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;

interface Props {
    projectId: number;
    overview: ExecutionOverview;
    notify: Notify;
}

const COLUMNS: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'DONE'];
const COLUMN_COLORS: Record<TaskStatus, string> = { TODO: '#90a4ae', IN_PROGRESS: '#1976d2', BLOCKED: '#6a1b9a', DONE: '#2e7d32' };
const PRIORITY_COLORS: Record<TaskPriority, 'default' | 'warning' | 'error'> = { LOW: 'default', MEDIUM: 'warning', HIGH: 'error' };

const today = () => format(new Date(), 'yyyy-MM-dd');
const EMPTY: TaskPayload = { title: '', description: '', status: 'TODO', priority: 'MEDIUM', due_date: null, workstream_id: null, activity_id: null, member_id: null };

/** Atividades da frente em ordem de cronograma, com recuo por nível (fase > atividade > subatividade). */
const flattenActivities = (nodes: ActivityNode[], depth = 0): { id: number; label: string }[] =>
    nodes.flatMap((n) => [{ id: n.id, label: `${'\u00a0\u00a0'.repeat(depth * 2)}${n.name}` }, ...flattenActivities(n.children, depth + 1)]);

/** Tarefas do dia a dia (a "Daily" da planilha): quadro por status, no estilo Jira. */
const TasksView: React.FC<Props> = ({ projectId, overview, notify }) => {
    const [tasks, setTasks] = useState<ExecutionTask[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ workstream: '', member: '', onlyLate: false, hideDone: false });
    const [dialog, setDialog] = useState<{ task: ExecutionTask | null } | null>(null);
    const [form, setForm] = useState<TaskPayload>(EMPTY);
    const [saving, setSaving] = useState(false);
    // Responsáveis: "m:<id>" = pessoa do time (alocada no projeto)
    const [assignees, setAssignees] = useState<string[]>([]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setTasks(await executionService.listTasks(projectId));
        } catch (error) {
            notify(errorMessage(error, 'Erro ao carregar as tarefas'), 'error');
        } finally {
            setLoading(false);
        }
    }, [projectId, notify]);

    useEffect(() => { load(); }, [load]);

    const isLate = (t: ExecutionTask) => t.status !== 'DONE' && !!t.due_date && t.due_date < today();
    const isDueToday = (t: ExecutionTask) => t.status !== 'DONE' && t.due_date === today();

    const visible = useMemo(() => tasks.filter((t) =>
        (!filters.workstream || String(t.workstream_id) === filters.workstream) &&
        (!filters.member || String(t.member_id) === filters.member) &&
        (!filters.onlyLate || isLate(t) || isDueToday(t)) &&
        (!filters.hideDone || t.status !== 'DONE')
    ), [tasks, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const lateCount = tasks.filter(isLate).length;
    const todayCount = tasks.filter(isDueToday).length;

    const openDialog = (task: ExecutionTask | null) => {
        setForm(task ? {
            title: task.title, description: task.description, status: task.status, priority: task.priority,
            due_date: task.due_date, workstream_id: task.workstream_id, activity_id: task.activity_id, member_id: task.member_id,
        } : { ...EMPTY, workstream_id: filters.workstream ? Number(filters.workstream) : null });
        setAssignees(task?.member_id ? [`m:${task.member_id}`] : []);
        setDialog({ task });
    };

    const save = async () => {
        if (!dialog) return;
        try {
            setSaving(true);
            const target = (value?: string) => !value ? { member_id: null }
                : value.startsWith('m:') ? { member_id: Number(value.slice(2)) } : { user_id: Number(value.slice(2)) };
            if (dialog.task) {
                await executionService.updateTask(dialog.task.id, { ...form, ...target(assignees[0]) });
                notify('Tarefa salva', 'success');
            } else {
                // Uma tarefa por pessoa escolhida (cada um acompanha a sua em "Minhas tarefas")
                const people = assignees.length ? assignees : [undefined];
                for (const person of people) {
                    await executionService.createTask(projectId, { ...form, ...target(person) });
                }
                notify(people.length > 1 ? `${people.length} tarefas criadas` : 'Tarefa salva', 'success');
            }
            setDialog(null);
            load();
        } catch (error) {
            notify(errorMessage(error, 'Erro ao salvar a tarefa'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const move = async (task: ExecutionTask, status: TaskStatus) => {
        try {
            const updated = await executionService.updateTask(task.id, { status });
            setTasks((prev) => prev.map((t) => (t.id === task.id ? updated : t)));
        } catch (error) {
            notify(errorMessage(error, 'Erro ao mover a tarefa'), 'error');
        }
    };

    const remove = async (task: ExecutionTask) => {
        if (!window.confirm(`Excluir a tarefa "${task.title}"?`)) return;
        try {
            await executionService.deleteTask(task.id);
            setTasks((prev) => prev.filter((t) => t.id !== task.id));
        } catch (error) {
            notify(errorMessage(error, 'Erro ao excluir a tarefa'), 'error');
        }
    };

    const members = overview.access.members;
    // Responsáveis = time do projeto (pessoas alocadas nas posições)
    const personOptions = members.map((m) => ({ value: `m:${m.id}`, label: m.name }));

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip color={lateCount ? 'error' : 'default'} label={`${lateCount} atrasada(s)`} />
                    <Chip color={todayCount ? 'warning' : 'default'} label={`${todayCount} para hoje`} />
                    <Chip variant="outlined" label={`${tasks.filter((t) => t.status !== 'DONE').length} em aberto`} />
                </Stack>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog(null)}>Nova tarefa</Button>
            </Box>

            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
                <TextField select size="small" label="Frente" value={filters.workstream} sx={{ minWidth: 180 }}
                    onChange={(e) => setFilters({ ...filters, workstream: e.target.value })}>
                    <MenuItem value="">Todas</MenuItem>
                    {overview.workstreams.map((w) => <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Responsável" value={filters.member} sx={{ minWidth: 180 }}
                    onChange={(e) => setFilters({ ...filters, member: e.target.value })}>
                    <MenuItem value="">Todos</MenuItem>
                    {members.map((m) => <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>)}
                </TextField>
                <FormControlLabel label="Só atrasadas / para hoje"
                    control={<Switch checked={filters.onlyLate} onChange={(e) => setFilters({ ...filters, onlyLate: e.target.checked })} />} />
                <FormControlLabel label="Ocultar concluídas"
                    control={<Switch checked={filters.hideDone} onChange={(e) => setFilters({ ...filters, hideDone: e.target.checked })} />} />
            </Stack>

            {!loading && tasks.length === 0 && (
                <Typography color="text.secondary" mb={2}>
                    Nenhuma tarefa ainda. Crie a primeira ou importe a aba "Daily" da planilha de controle.
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, alignItems: 'flex-start' }}>
                {COLUMNS.filter((c) => !(filters.hideDone && c === 'DONE')).map((column) => {
                    const items = visible.filter((t) => t.status === column);
                    return (
                        <Paper key={column} variant="outlined"
                            sx={{ flex: '1 0 260px', minWidth: 260, p: 1.5, bgcolor: 'grey.50', borderTop: `3px solid ${COLUMN_COLORS[column]}` }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {TASK_STATUS_LABELS[column]} <Typography component="span" variant="caption" color="text.secondary">({items.length})</Typography>
                            </Typography>
                            <Stack spacing={1}>
                                {items.map((task) => (
                                    <Card key={task.id} variant="outlined"
                                        sx={{ borderLeft: isLate(task) ? '3px solid #c62828' : isDueToday(task) ? '3px solid #f9a825' : undefined }}>
                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{task.title}</Typography>
                                            {task.description && (
                                                <Typography variant="caption" color="text.secondary" display="block">{task.description}</Typography>
                                            )}
                                            <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" useFlexGap>
                                                {task.assignee_name && <Chip size="small" label={task.assignee_name} />}
                                                {task.workstream_name && <Chip size="small" variant="outlined" label={task.workstream_name} />}
                                                {task.activity_name && <Chip size="small" variant="outlined" color="primary" label={`Atividade: ${task.activity_name}`} />}
                                                <Chip size="small" color={PRIORITY_COLORS[task.priority]} variant="outlined" label={TASK_PRIORITY_LABELS[task.priority]} />
                                                {task.due_date && (
                                                    <Chip size="small" color={isLate(task) ? 'error' : isDueToday(task) ? 'warning' : 'default'}
                                                        label={`${isLate(task) ? 'Venceu ' : 'Prazo '}${formatDate(task.due_date)}`} />
                                                )}
                                            </Stack>
                                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                                {/* Mover entre colunas pelo seletor (funciona no celular e por teclado) */}
                                                <TextField select size="small" value={task.status} sx={{ flex: 1 }}
                                                    inputProps={{ 'aria-label': `Status da tarefa ${task.title}` }}
                                                    onChange={(e) => move(task, e.target.value as TaskStatus)}>
                                                    {COLUMNS.map((s) => <MenuItem key={s} value={s}>{TASK_STATUS_LABELS[s]}</MenuItem>)}
                                                </TextField>
                                                <Tooltip title="Editar">
                                                    <IconButton size="small" aria-label={`Editar ${task.title}`} onClick={() => openDialog(task)}><EditIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton size="small" aria-label={`Excluir ${task.title}`} onClick={() => remove(task)}><DeleteIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                                {items.length === 0 && <Typography variant="caption" color="text.secondary">Nenhuma tarefa.</Typography>}
                            </Stack>
                        </Paper>
                    );
                })}
            </Box>

            <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{dialog?.task ? 'Editar tarefa' : 'Nova tarefa'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField fullWidth required label="Título" value={form.title || ''} autoFocus
                                onChange={(e) => setForm({ ...form, title: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={2} label="Descrição" value={form.description || ''}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select fullWidth label={dialog?.task ? 'Responsável' : 'Responsáveis'}
                                SelectProps={{
                                    multiple: !dialog?.task,
                                    renderValue: (selected) => {
                                        const values = (Array.isArray(selected) ? selected : [selected]) as string[];
                                        return values.filter(Boolean).map((v) => personOptions.find((o) => o.value === v)?.label || v).join(', ') || 'Sem responsável';
                                    },
                                    displayEmpty: true,
                                }}
                                value={dialog?.task ? (assignees[0] || '') : assignees}
                                onChange={(e) => {
                                    const value = e.target.value as unknown as string | string[];
                                    setAssignees(Array.isArray(value) ? value : value ? [value] : []);
                                }}
                                helperText={dialog?.task ? '' : 'Escolha uma ou mais pessoas: cada uma recebe a sua tarefa'}>
                                {dialog?.task && <MenuItem value="">Sem responsável</MenuItem>}
                                {personOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                            {!dialog?.task && members.length > 0 && (
                                <Button size="small" onClick={() => setAssignees(members.map((m) => `m:${m.id}`))}>
                                    Todos da equipe ({members.length})
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select fullWidth label="Frente" value={form.workstream_id ? String(form.workstream_id) : ''}
                                onChange={(e) => setForm({ ...form, workstream_id: e.target.value ? Number(e.target.value) : null, activity_id: null })}>
                                <MenuItem value="">Sem frente</MenuItem>
                                {overview.workstreams.map((w) => <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            {/* Liga a tarefa a uma atividade/subatividade do cronograma (a frente acompanha) */}
                            <TextField select fullWidth label="Atividade do cronograma (opcional)"
                                value={form.activity_id ? String(form.activity_id) : ''} disabled={!form.workstream_id}
                                helperText={form.workstream_id ? 'A tarefa aparece contada nessa atividade no cronograma' : 'Escolha a frente para listar as atividades'}
                                onChange={(e) => setForm({ ...form, activity_id: e.target.value ? Number(e.target.value) : null })}>
                                <MenuItem value="">Sem atividade</MenuItem>
                                {flattenActivities(overview.workstreams.find((w) => w.id === form.workstream_id)?.phases || [])
                                    .map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth type="date" label="Prazo" InputLabelProps={{ shrink: true }} value={form.due_date || ''}
                                onChange={(e) => setForm({ ...form, due_date: e.target.value || null })} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField select fullWidth label="Prioridade" value={form.priority || 'MEDIUM'}
                                onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}>
                                {(Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[]).map((p) => <MenuItem key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField select fullWidth label="Status" value={form.status || 'TODO'}
                                onChange={(e) => setForm({ ...form, status: e.target.value as TaskStatus })}>
                                {COLUMNS.map((s) => <MenuItem key={s} value={s}>{TASK_STATUS_LABELS[s]}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={save} disabled={saving || !(form.title || '').trim()}>Salvar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TasksView;
